import { TraderDashboardModel } from '@/features/dashboardTrader/model/types'
import { BalanceChangeBadge, BaseBadge } from '@/shared/components'
import {
  InfoCard,
  InformationStatisticCards,
} from '@/shared/components/InformationStatisticCards/InformationStatisticCards'
import { formatUSDT } from '@/shared/lib/currency/formatUSDT'
import { formatTimeFromSeconds } from '@/shared/lib/date'
import { StatisticTabNames } from '@/shared/lib/models/tabsStatisticOptions'
import { formatterRubWithCurrency } from '@/shared/utils'
import { ProgressInfo } from '../ProgressInfo/ProgressInfo'
import { getPercentChange } from '@/shared/components/BalanceChangeBadge/lib'

type PanelProps = {
  data: TraderDashboardModel | undefined
  type: StatisticTabNames
}

export const StatisticDashboardPanel = ({ data, type }: PanelProps) => {
  const currentData = type === 'payIn' ? data?.topup : data?.withdraw

  const isPayIn = type === 'payIn'
  const isPayOut = type === 'payOut'

  const infoCards: InfoCard[] = currentData
    ? [
        { id: 1, key: 'Прибыль', value: formatUSDT(currentData.commissionAmount) },
        {
          id: 2,
          key: 'Конверсия',
          value:
            currentData.totalCount > 0
              ? `${((currentData.paidCount / currentData.totalCount) * 100).toFixed(1)}%`
              : '0%',
        },
        {
          id: 3,
          key: isPayIn ? 'Входящие сделки' : 'Исходящие заявки',
          value: `${currentData.paidCount} / ${currentData.totalCount}`,
        },
        ...(isPayOut && data?.topup
          ? [
              {
                id: 4,
                key: 'Контрагенты',
                value: `${data.topup.paidMerchantsCount} / ${data.topup.totalMerchantsCount}`,
              },
            ]
          : []),
        {
          id: 5,
          key: 'Время подтверждения сделки',
          value: formatTimeFromSeconds(currentData.averageConfirmSeconds ?? 0),
        },
        {
          id: 6,
          key: 'Средняя сумма заявки',
          value:
            currentData.totalCount > 0
              ? `${formatterRubWithCurrency.format(
                  (isPayIn ? currentData.paidAmountFrom : currentData.paidAmountTo) /
                    currentData.paidCount
                )}`
              : '0 RUB',
        },
      ]
    : [
        { id: 1, key: 'Прибыль', value: '0 USDT' },
        { id: 2, key: 'Конверсия', value: '0%' },
        { id: 3, key: isPayIn ? 'Входящие сделки' : 'Исходящие заявки', value: '0 / 0' },
        ...(isPayOut ? [{ id: 4, key: 'Контрагенты', value: '0 / 0' }] : []),
        { id: 5, key: 'Время подтверждения сделки', value: '0 мин' },
        { id: 6, key: 'Средняя сумма заявки', value: '0 RUB' },
      ]

  const totalBalance =
    (isPayIn ? data?.topupTotalRequisitLimit.totalMaxDailyLimit : currentData?.totalAmountTo) ?? 0

  const currentBalance = isPayIn ? currentData?.paidAmountFrom : currentData?.paidAmountTo

  const percentage = (
    currentBalance && totalBalance > 0 ? (currentBalance / totalBalance) * 100 : 0
  ).toFixed(2)

  const percentChange = data?.yesterdayTopup
    ? getPercentChange(data?.yesterdayTopup.paidAmountFrom ?? 0, currentData?.paidAmountFrom ?? 0)
    : 0

  return (
    <div>
      <div className="flex md:flex-row flex-col gap-[10px] mb-4">
        <BaseBadge type="primary">
          Выполнен на {percentage === '0.00' ? '0' : percentage}%
        </BaseBadge>
        {typeof percentChange === 'number' &&
          percentChange !== 0 &&
          isPayIn &&
          data?.yesterdayTopup && (
            <BalanceChangeBadge
              currentBalance={currentData?.paidAmountFrom ?? 0}
              yesterdayBalance={data?.yesterdayTopup.paidAmountFrom ?? 0}
            />
          )}
      </div>

      <ProgressInfo
        currentBalance={currentBalance ?? 0}
        totalBalance={totalBalance}
        className="mb-[40px]"
        type={type}
      />

      <InformationStatisticCards infoCards={infoCards} />
    </div>
  )
}
